import { PaginationProps, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";
import { useContext, useEffect, useMemo, useState } from "react";
import ConfirmPopup from "../../commons/components/ConfirmPopup/ConfirmPopup";
import CustomInput from "../../commons/components/CustomInput/CustomInput";
import CustomPagination from "../../commons/components/CustomPagination";
import CustomTable from "../../commons/components/CustomTable";
import PrimaryButton from "../../commons/components/PrimaryButton/PrimaryButton";

import CustomIcon from "../../commons/components/CustomIcon/CustomIcon";
import {
  DELETE_MEMBER_MERCHANT,
  MERCHANT_MEMBER_LIST,
  MERCHANT_MEMBER_ONBOARD,
  RESEND_EMAIL_MEMBER_MERCHANT,
  SEND_EMAIL_RESET_PASSWORD,
} from "../../commons/constants/api-urls";
import useFetchList from "../../commons/hooks/useFetchList";
import { EditIcon, SearchNormalIcon, SendIcon, TrashIcon } from "../../commons/resources";
import defaultAxios from "../../commons/utils/axios";
import AddNewMember from "./AddNewMember/AddNewMember";
import styles from "./MerchantMember.module.scss";
import { AppContext } from "../../contexts/AppContext";

const MerchantMember: React.FC = () => {
  const { handleFindStore, getAllStoresForSearch } = useContext(AppContext);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [pageOnBoard, setPageOnBoard] = useState(1);
  const [perPageOnBoard, setPerPageOnBoard] = useState(10);

  const [keyword, setKeyword] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [selectedUser, setSelectedUser] = useState<MerchantMember>();
  const [selectedUserToDelete, setSelectedUserToDelete] = useState<MerchantMember>();

  const [openConfirmPopup, setOpenConfirmPopup] = useState(false);
  const [openAddNewMemberPopup, setOpenAddNewMemberPopup] = useState(false);
  const [openModalResendEmail, setOpenModalResendEmail] = useState(false);
  const [openModalResendPw, setOpenModalResendPw] = useState(false);
  const onShowSizeChange: PaginationProps["onShowSizeChange"] = (current, pageSize) => {
    setPerPage(pageSize || 10);
  };

  const onShowSizeChangeOnboard: PaginationProps["onShowSizeChange"] = (current, pageSize) => {
    setPerPageOnBoard(pageSize || 10);
  };

  useEffect(() => {
    getAllStoresForSearch();
  }, []);

  const { data, total, refresh } = useFetchList<any>(MERCHANT_MEMBER_LIST(), {
    page,
    perPage,
    status: 1,
    search: keyword.trim(),
  });

  const {
    data: dataMemberOnboard,
    total: totalMemberOnboard,
    refresh: refreshMemberOnboard,
  } = useFetchList<any>(MERCHANT_MEMBER_ONBOARD(), {
    page: pageOnBoard,
    perPage: perPageOnBoard,
    name: keyword.trim(),
  });

  const dataTableMember = useMemo(() => {
    if (!data) return [];
    return data.map(item => ({
      key: item.id,
      email: item.email,
      phone: item.phone,
      id: item.id,
      status: item.status,
      create_time: item.create_time,
      merchant_stores_ids: item.storeIds,
      merchant_stores_names: item.storeIds.map((item: string) => {
        if (item) {
          return handleFindStore(item);
        }
        return undefined;
      }),
      role: item.role && item.role ? item.role[0] : "",
      merchant_member_id: item.id,
    }));
  }, [data, handleFindStore]);

  const dataTableMemberOnboard = useMemo(() => {
    if (!dataMemberOnboard) return [];
    return dataMemberOnboard.map(item => ({
      key: item.id,
      email: item.email,
      phone: item.phone,
      id: item.id,
      status: item.status,
      create_time: item.create_time,
      merchant_stores_ids: item.storeIds,
      merchant_stores_names: item.storeIds.map((item: string) => {
        if (item) {
          return handleFindStore(item) || "";
        }
        return undefined;
      }),
      role: item.role && item.role ? item.role[0] : "",
      merchant_member_id: item.id,
    }));
  }, [dataMemberOnboard, handleFindStore]);

  const handleCancel = () => {
    setOpenAddNewMemberPopup(false);
    if (isEdit) {
      setIsEdit(false);
      setSelectedUser(undefined);
    }
  };

  const handleEdit = (member: MerchantMember) => {
    setSelectedUser(member);
    setOpenAddNewMemberPopup(true);
    setIsEdit(true);
  };
  const handleDelete = (memberId: MerchantMember) => {
    setSelectedUserToDelete(memberId);
    setOpenConfirmPopup(true);
  };
  const handleResendEmail = (member: MerchantMember) => {
    setSelectedUser(member);
    setOpenModalResendEmail(true);
  };

  const handleSendEmailResetPw = (member: MerchantMember) => {
    setSelectedUser(member);
    setOpenModalResendPw(true);
  };

  async function handleDeleteMember() {
    setOpenConfirmPopup(false);
    try {
      const bodyDeleteMember = {
        status: 0,
        merchant_member_id: Number(selectedUserToDelete?.id),
      };
      await defaultAxios.patch(DELETE_MEMBER_MERCHANT(), bodyDeleteMember);
      message.success("Delete member successfully");
      refresh();
    } catch (error) {
      message.error("Delete member failed");
    }
  }

  async function handleResendEmailToMember() {
    setOpenModalResendEmail(false);
    try {
      await defaultAxios.get(RESEND_EMAIL_MEMBER_MERCHANT(selectedUser!.id));
      setSelectedUser(undefined);
      message.success("Resend email successfully");
      refreshMemberOnboard();
    } catch (error) {
      message.error("Resend email failed");
    }
  }

  async function handleResendEmailResetPassword() {
    setOpenModalResendPw(false);
    try {
      await defaultAxios.post(SEND_EMAIL_RESET_PASSWORD(), { email: selectedUser?.email });
      setSelectedUser(undefined);
      message.success("Resend email successfully");
    } catch (error) {
      message.error("Resend email failed");
    }
  }

  function handleRefresh() {
    if (isEdit) {
      refresh();
    } else {
      refreshMemberOnboard();
    }
  }

  const columns: ColumnsType<MerchantMember> = [
    {
      title: "No",
      dataIndex: "no",
      width: 60,
      render: (text, record, index) => {
        return page === 1 ? index + 1 : (page - 1) * perPage + (index + 1);
      },
    },
    {
      title: "Store Name",
      dataIndex: "merchant_stores_names",
      render: (text, record) => {
        return (
          <div className={`${styles.product} ${styles.pointer}`}>
            {record.merchant_stores_names && record.merchant_stores_names.length > 0
              ? record.merchant_stores_names[0]
              : ""}
          </div>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (text, record) => {
        return <div className={`${styles.product} ${styles.pointer}`}>{text}</div>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text, record) => {
        return <div className={text === 1 ? styles.active : styles.inactive}>{text === 1 ? "Active" : "Inactive"}</div>;
      },
    },
    {
      title: "Date created",
      dataIndex: "create_time",
      render: (text, record) => {
        return (
          <div className={styles.pointer}>
            {moment(text).format("DD/MM/YYYY") + ", " + moment(text).format("HH:mm")}
          </div>
        );
      },
    },

    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <div className={styles.actions}>
          <div className={styles.edit}>
            <EditIcon onClick={() => handleEdit(record)} />
          </div>
          <div className={styles.delete} onClick={() => handleDelete(record)}>
            <TrashIcon />
          </div>
          <div className={styles.edit}>
            <SendIcon onClick={() => handleSendEmailResetPw(record)} />
          </div>
        </div>
      ),
    },
  ];

  const columnsMerchantMemberOnboard: ColumnsType<MerchantMember> = [
    {
      title: "No",
      dataIndex: "no",
      width: 60,
      render: (text, record, index) => {
        return pageOnBoard === 1 ? index + 1 : (pageOnBoard - 1) * perPageOnBoard + (index + 1);
      },
    },
    {
      title: "Store Name",
      dataIndex: "merchant_stores_names",
      render: (text, record) => {
        return (
          <div className={`${styles.product} ${styles.pointer}`}>
            {record.merchant_stores_names && record.merchant_stores_names.length > 0
              ? record.merchant_stores_names[0]
              : ""}
          </div>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (text, record) => {
        return <div className={`${styles.product} ${styles.pointer}`}>{text}</div>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text, record) => {
        return <div className={styles.active}>{"Pending"}</div>;
      },
    },
    {
      title: "Date created",
      dataIndex: "create_time",
      render: (text, record) => {
        return (
          <div className={styles.pointer}>
            {moment(text).format("DD/MM/YYYY") + ", " + moment(text).format("HH:mm")}
          </div>
        );
      },
    },

    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <div className={styles.actions}>
          <div className={styles.edit}>
            <SendIcon onClick={() => handleResendEmail(record)} />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <div className={styles.titleWrapper}>
        <div className={styles.title}>Merchant Member</div>
        <PrimaryButton className={styles.addProductBtn} onClick={() => setOpenAddNewMemberPopup(true)}>
          Add new member
        </PrimaryButton>
      </div>
      <CustomInput
        onChange={event => setKeyword(event.target.value)}
        onBlur={event => setKeyword(event.target.value.trim())}
        className={styles.inputSearch}
        type="search"
        placeholder="Search for member"
        prefix={<CustomIcon icon={SearchNormalIcon} width={20} stroke="currentColor" />}
      />
      <div className={styles.productPanel}>
        <div className={styles.paginationWrapper}>
          <h2>Merchant Member</h2>
          <CustomPagination
            defaultCurrent={1}
            onChange={page => setPage(page)}
            showSizeChanger
            total={total}
            onShowSizeChange={onShowSizeChange}
            current={page}
            pageSize={perPage}
          />
        </div>
        <CustomTable pagination={false} columns={columns} dataSource={dataTableMember} scroll={{ x: 400 }} />
      </div>
      <div className={styles.productPanel}>
        <div className={styles.paginationWrapper}>
          <h2>Pending Merchant Member </h2>
          <CustomPagination
            defaultCurrent={1}
            onChange={page => setPageOnBoard(page)}
            showSizeChanger
            total={totalMemberOnboard}
            onShowSizeChange={onShowSizeChangeOnboard}
            current={pageOnBoard}
            pageSize={perPageOnBoard}
          />
        </div>
        <CustomTable
          pagination={false}
          columns={columnsMerchantMemberOnboard}
          dataSource={dataTableMemberOnboard}
          scroll={{ x: 400 }}
        />
      </div>
      <AddNewMember
        member={selectedUser}
        open={openAddNewMemberPopup}
        isEdit={isEdit}
        onCancel={handleCancel}
        refresh={handleRefresh}
      />
      <ConfirmPopup
        open={openConfirmPopup}
        onCancel={() => {
          setOpenConfirmPopup(false);
          setSelectedUser(undefined);
        }}
        title="Delete member"
        description={`Are you sure you want to delete ${selectedUserToDelete?.email}?`}
        onConfirm={() => {
          handleDeleteMember();
        }}
      />

      <ConfirmPopup
        open={openModalResendEmail}
        onCancel={() => {
          setOpenModalResendEmail(false);
          setSelectedUser(undefined);
        }}
        title="Resend email"
        description={`Are you sure you want to resend email to ${selectedUser?.email}?`}
        onConfirm={() => {
          handleResendEmailToMember();
        }}
      />

      <ConfirmPopup
        open={openModalResendPw}
        onCancel={() => {
          setOpenModalResendPw(false);
          setSelectedUser(undefined);
        }}
        title="Resend email"
        description={`Are you sure you want to resend email to ${selectedUser?.email}?`}
        onConfirm={() => {
          handleResendEmailResetPassword();
        }}
      />
    </div>
  );
};

export default MerchantMember;
